import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  Button,
  Checkbox,
  ContentPanel,
  FillPageIcon,
  FillSpreadIcon,
  Label,
  NumberInput,
  PositioningIcon,
  SelectList,
  ShrinkPictureIcon,
} from '@taopix/taopix-design-system';
import {
  AssetType,
  BoxFitMode,
  BoxType,
  BoxTypeShape,
  CoordinateScale,
  NumBool,
} from '@taopix/taopix-core';
import {useTaopix} from '../../../Context/TaopixContext';
import classNames from 'classnames';
import {useExperience} from '../../../Experience/ExperienceProvider';
import {ListItemDataType} from '@taopix/taopix-design-system/dist/types/Components/SelectList/SelectList';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import ToolPaletteSection from '../../../ToolPalette/ToolPaletteSection';
import PaletteInformationMessage from '../PaletteInformationMessage';

export type AdvancedMeasurements = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export type MinPosition = {
  top: number;
  left: number;
  right: number;
  bottom: number;
};

export type MinMaxSize = {
  minWidth: number;
  maxWidth: number;
  minHeight: number;
  maxHeight: number;
};

export type SizeAndPositionProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

type SizeAndPositonExperienceValues = {
  fillPageSpread: boolean;
  fitPictureToBox: boolean;
  nudge: boolean;
  advanced: boolean;
  lockBoxProportions: boolean;
};

const SizeAndPosition = ({sectionKey, item}: SizeAndPositionProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation();

  const {experience} = useExperience();
  const {pictureBoxOptions, textBoxOptions, stickerOptions, shapeBoxOptions} = experience.EDITOR;

  const stateManager = window.Taopix.Core.get('stateManager');

  const productLayout = stateManager.getLayout();

  const display = stateManager.getPageDisplayProperties();

  const [showSection, setShowSection] = useState<boolean>(false);
  const [disableSection, setDisableSection] = useState<boolean>(false);
  const [notAvailableMessage, setNotAvailableMessage] = useState<string>();

  const [lockProportionsKey, setLockProportionsKey] = useState<string>('');

  const [advancedMeasurements, setAdvancedMeasurements] = useState<AdvancedMeasurements>({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  const [minPositionValues, setMinPositionValues] = useState<MinPosition>({
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  });

  const [minMaxSizeValues, setMinMaxSizeValues] = useState<MinMaxSize>({
    minWidth: 0,
    maxWidth: 0,
    minHeight: 0,
    maxHeight: 0,
  });

  const [selectedMeasurment, setSelectedMeasurement] = useState<CoordinateScale>(
    experience.SETTINGS.units.units === 'mm'
      ? CoordinateScale.Millimetres
      : experience.SETTINGS.units.units === 'i'
      ? CoordinateScale.Inches
      : experience.SETTINGS.units.units === 'cm'
      ? CoordinateScale.Centimetres
      : CoordinateScale.Inches
  );

  const [sizeAndPositionOptions, setSizeAndPositionOptions] =
    useState<SizeAndPositonExperienceValues>({
      fillPageSpread: true, // protected size/position
      fitPictureToBox: false, // protect content
      nudge: true, // protected position
      advanced: true, // protected size/position
      lockBoxProportions: true, // protected constrain props
    });

  // Track that the size has changed.
  // This is part of the hack to force React to re-render the numberInput components.
  const sizeUpdateTracker = React.useRef({
    version: 0,
  });

  const measurementListOptions: Array<ListItemDataType> = [
    {
      label: t('*:str_LabelInches'),
      value: CoordinateScale.Inches as unknown as string,
    },
    {
      label: t('*:str_LabelMillimetres'),
      value: CoordinateScale.Millimetres as unknown as string,
    },
    {
      label: t('*:str_LabelCentimetres'),
      value: CoordinateScale.Centimetres as unknown as string,
    },
  ];

  const isActiveInExperience = (item: ComputedPageItem) => {
    const boxType = item.type;
    // Return whether or not this option is enabled in the experience for the selected item
    if (
      boxType === BoxType.Image &&
      item?.asset?.assetType !== AssetType.Background &&
      item?.asset?.assetType !== AssetType.ScrapbookImage
    ) {
      setSizeAndPositionOptions({
        fillPageSpread: pictureBoxOptions.fillPageSpread,
        fitPictureToBox: pictureBoxOptions.fitPictureToBox,
        nudge: pictureBoxOptions.nudge,
        advanced: pictureBoxOptions.advanced,
        lockBoxProportions: pictureBoxOptions.lockBoxProportions,
      });
      return pictureBoxOptions.sizeAndPosition;
    }
    if (boxType === BoxType.Text) {
      setSizeAndPositionOptions({
        fillPageSpread: textBoxOptions.fillPageSpread,
        fitPictureToBox: false,
        nudge: textBoxOptions.nudge,
        advanced: textBoxOptions.advanced,
        lockBoxProportions: textBoxOptions.lockBoxProportions,
      });
      return textBoxOptions.sizeAndPosition;
    }
    if (AssetType.ScrapbookImage === item.hiresfiletype) {
      setSizeAndPositionOptions({
        fillPageSpread: stickerOptions.fillPageSpread,
        fitPictureToBox: false,
        nudge: stickerOptions.nudge,
        advanced: stickerOptions.advanced,
        lockBoxProportions: stickerOptions.lockBoxProportions,
      });
      return stickerOptions.sizeAndPosition;
    }
    if (
      boxType === BoxType.Shape &&
      [BoxTypeShape.Line, BoxTypeShape.Oval, BoxTypeShape.Polygon, BoxTypeShape.Rect].includes(
        item?.shape
      )
    ) {
      setSizeAndPositionOptions({
        fillPageSpread: shapeBoxOptions.fillPageSpread,
        fitPictureToBox: false,
        nudge: shapeBoxOptions.nudge,
        advanced: shapeBoxOptions.advanced,
        lockBoxProportions: shapeBoxOptions.lockBoxProportions,
      });
      return shapeBoxOptions.sizeAndPosition;
    } else {
      return false;
    }
  };

  // if the item is fully protected then we can disable the palette
  const isProtected = (item: ComputedPageItem) => {
    return (
      NumBool.True === item?.protectcontents &&
      NumBool.True === item?.protectconstrainproportions &&
      NumBool.True === item?.protecthorizontalsize &&
      NumBool.True === item?.protectverticalsize &&
      NumBool.True === item?.protecthorizontalposition &&
      NumBool.True === item?.protectverticalposition
    );
  };

  useEffect(() => {
    // This is an advanced option, so check whether advanced editing is on or toggleable
    const canShowAdvancedOptions = ui.state.canEditPageLayout;

    // If there is a selected item
    if (item && item.ref !== -1) {
      // Show or hide this section based on the relevant experience and advanced editing configuration
      setShowSection(isActiveInExperience(item) && canShowAdvancedOptions);
      // Enable or disable this section based on the item's protection
      setDisableSection(
        isProtected(item) || (item.errors?.imageMissing as boolean) || ui.state.swapActive
      );
      // Set the message to 'not available' in case the section was hidden or disabled
      setNotAvailableMessage(t('toolpalette:str_OptionNotAvailableForSelectedItem'));
    } else {
      // If no item is selected, hide the section button
      setShowSection(false);
      // And show the 'please select an item' message
      setNotAvailableMessage(t('toolpalette:str_MessageSelectAnItemToViewOptions'));
    }
  }, [
    item?.asset,
    item?.asset?.assetType,
    item?.type,
    item?.image,
    item?.shape,
    ui.state.swapActive,
  ]);

  useEffect(() => {
    setLockProportionsKey(`lockProportions-${item?.ref}-${item?.boxconstrainproportions === 1}`);
  }, [item?.ref, item?.boxconstrainproportions]);

  useEffect(() => {
    if (!item?.hasOwnProperty('ref') || item?.ref === -1 || !display) {
      return;
    }

    setAdvancedMeasurements(current => {
      return {
        ...current,
        ...app.core.get('utils').getSize(item, selectedMeasurment, display),
        ...app.core.get('utils').getPosition(item, selectedMeasurment, display),
      };
    });
  }, [
    item?.ref,
    item?.boxleft,
    item?.boxtop,
    item?.group?.box?.height,
    item?.group?.box?.width,
    item?.masterrotation,
  ]);

  // This is a hack to force the inputs to update in the event the height/width values revert due
  // to an invalid value being entered.
  // React won't detect that the value has actually been changed.
  const itemSize = {height: 0, width: 0};
  if (item) {
    // Get the real size from the item (in correct measurement unit).
    const realSize = app.core.get('utils').getSize(item, selectedMeasurment, display);
    itemSize.height = realSize.height;
    itemSize.width = realSize.width;
  }

  useEffect(() => {
    if (!item?.hasOwnProperty('ref') || item?.ref === -1 || !display) {
      return;
    }

    setMinMaxSizeValues(current => {
      return {
        ...current,
        ...app.core.get('utils').getMinMaxBoxSize(display, {
          units: selectedMeasurment,
          shouldScale: true,
        }),
      };
    });
  }, [display, item?.ref, selectedMeasurment]);

  useEffect(() => {
    if (!item?.hasOwnProperty('ref') || item?.ref === -1 || !display) {
      return;
    }

    setMinPositionValues(current => {
      return {
        ...current,
        ...app.core.get('utils').getMinPosition(item?.boxheight, item?.boxwidth, display, {
          units: selectedMeasurment,
          shouldScale: true,
        }),
      };
    });
  }, [item, item?.boxheight, item?.boxwidth]);

  useEffect(() => {
    if (!item?.hasOwnProperty('ref') || item?.ref === -1 || !display) {
      return;
    }

    setAdvancedMeasurements(current => ({
      ...current,
      ...app.core.get('utils').getSize(item, selectedMeasurment, display),
      ...app.core.get('utils').getPosition(item, selectedMeasurment, display),
    }));
  }, [selectedMeasurment]);

  const sectionButtonClasses = classNames('lg:py-md');

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'px-md',
    'space-y-sm',
    'lg:flex-1',
    'lg:pb-lg',
    'sm:pb-md',
    'sm:h-mobilePaletteContentHeight',
    'sm:max-w-full',
    'overflow-y-auto'
  );

  const getPositionWithoutBleed = (position: number): number => {
    return Number(position) - Number(display.pageBleed);
  };

  if (
    ui.state.palette.section !== 'box-settings-palette' &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}`
  ) {
    return null;
  }

  return (
    <ToolPaletteSection
      id={'sizeAndPositionPalette'}
      title={t('boxpalettes:str_TitleSizeAndPosition')}
      buttonIcon={<PositioningIcon />}
      sectionKey={sectionKey}
      buttonClasses={sectionButtonClasses}
      useFocusMode
      subSection={true}
      showButton={showSection}
      disabled={disableSection}
      buttonLayout="horizontal"
      parentselected={true}
    >
      <div className={wrapperClasses} tabIndex={-1}>
        {!showSection || disableSection ? (
          // If the palette button has been hidden or disabled (by the experience, box protection or nothing is selected),
          // show the relevant 'unavailable' message in the palette in case the palette was left open when its status changed
          <PaletteInformationMessage message={notAvailableMessage} />
        ) : (
          // Only show the sub options if the palette is allowed in the experience and the content is not protected
          <>
            {sizeAndPositionOptions.fillPageSpread && (
              <ContentPanel heading={t('boxpalettes:str_TitleFillPage')}>
                <Button
                  data-testid={'box-settings-sizeandposition-fillpage-button'}
                  className={'w-full mb-xs'}
                  label={t('boxpalettes:str_TitleFillPage')}
                  buttonStyle={'tertiary'}
                  startIcon={<FillPageIcon />}
                  labelAlignment={'left'}
                  size={'large'}
                  disabled={
                    NumBool.True === item?.protecthorizontalsize ||
                    NumBool.True === item?.protectverticalsize ||
                    NumBool.True === item?.protecthorizontalposition ||
                    NumBool.True === item?.protectverticalposition ||
                    0 !== item?.groupref
                  }
                  onClick={() => {
                    app.core
                      .dispatch('SHAPE.ALIGN_ITEM', {mode: BoxFitMode.TO_PAGE})
                      .catch(error => {
                        console.error(error);
                      });
                  }}
                />
                {productLayout?.spreads === 1 && (
                  <Button
                    data-testid={'box-settings-sizeandposition-fillspread-button'}
                    className={'w-full mb-xs'}
                    label={t('boxpalettes:str_TitleFillSpread')}
                    buttonStyle={'tertiary'}
                    startIcon={<FillSpreadIcon />}
                    labelAlignment={'left'}
                    size={'large'}
                    disabled={
                      NumBool.True === item?.protecthorizontalsize ||
                      NumBool.True === item?.protectverticalsize ||
                      NumBool.True === item?.protecthorizontalposition ||
                      NumBool.True === item?.protectverticalposition ||
                      0 !== item?.groupref
                    }
                    onClick={() => {
                      app.core
                        .dispatch('SHAPE.ALIGN_ITEM', {mode: BoxFitMode.TO_SPREAD})
                        .catch(error => {
                          console.error(error);
                        });
                    }}
                  />
                )}
              </ContentPanel>
            )}
            {/* only show for populated pictureboxes */}
            {BoxType.Image === item?.type &&
              [AssetType.Productcollection, AssetType.User].includes(item.hiresfiletype) &&
              item?.hirespath !== '' &&
              sizeAndPositionOptions.fitPictureToBox && (
                <ContentPanel heading={t('boxpalettes:str_TitleFitPicture')}>
                  <Button
                    data-testid={'box-settings-sizeandposition-fitpicture-button'}
                    className={'w-full mb-xs'}
                    label={t('boxpalettes:str_LabelFitPictureToBox')}
                    buttonStyle={'tertiary'}
                    startIcon={<ShrinkPictureIcon />}
                    labelAlignment={'left'}
                    size={'large'}
                    onClick={() => {
                      app.core
                        .dispatch('SHAPE.ALIGN_ITEM', {mode: BoxFitMode.TO_BOX})
                        .catch(error => {
                          console.error(error);
                        });
                    }}
                    disabled={NumBool.True === item?.protectcontents}
                  />
                </ContentPanel>
              )}
            {sizeAndPositionOptions.nudge && (
              <ContentPanel heading={t('boxpalettes:str_TitleNudge')}>
                <div className={'flex space-x-xxs'}>
                  <Button
                    data-testid={'box-settings-sizeandposition-nudgeleft-button'}
                    label={t('boxpalettes:str_LabelNudgeLeft')}
                    hideLabel
                    buttonStyle={'tertiary'}
                    startIcon={<ArrowLeftIcon />}
                    labelAlignment={'centre'}
                    disabled={NumBool.True === item?.protecthorizontalposition}
                    onClick={() => {
                      app.core
                        .dispatch('SHAPE.MOVE_SHAPE', {
                          delta: {x: -1, y: 0},
                          scale: 1,
                        })
                        .catch(error => {
                          console.error(error);
                        });
                    }}
                  />
                  <Button
                    data-testid={'box-settings-sizeandposition-nudgeright-button'}
                    label={t('boxpalettes:str_LabelNudgeRight')}
                    hideLabel
                    buttonStyle={'tertiary'}
                    startIcon={<ArrowRightIcon />}
                    labelAlignment={'centre'}
                    disabled={NumBool.True === item?.protecthorizontalposition}
                    onClick={() => {
                      app.core
                        .dispatch('SHAPE.MOVE_SHAPE', {
                          delta: {x: 1, y: 0},
                          scale: 1,
                        })
                        .catch(error => {
                          console.error(error);
                        });
                    }}
                  />
                  <Button
                    data-testid={'box-settings-sizeandposition-nudgeup-button'}
                    label={t('boxpalettes:str_LabelNudgeUp')}
                    hideLabel
                    buttonStyle={'tertiary'}
                    startIcon={<ArrowUpIcon />}
                    labelAlignment={'centre'}
                    disabled={NumBool.True === item?.protectverticalposition}
                    onClick={() => {
                      app.core
                        .dispatch('SHAPE.MOVE_SHAPE', {
                          delta: {x: 0, y: -1},
                          scale: 1,
                        })
                        .catch(error => {
                          console.error(error);
                        });
                    }}
                  />
                  <Button
                    data-testid={'box-settings-sizeandposition-nudgedown-button'}
                    label={t('boxpalettes:str_LabelNudgeDown')}
                    hideLabel
                    buttonStyle={'tertiary'}
                    startIcon={<ArrowDownIcon />}
                    labelAlignment={'centre'}
                    disabled={NumBool.True === item?.protectverticalposition}
                    onClick={() => {
                      app.core
                        .dispatch('SHAPE.MOVE_SHAPE', {
                          delta: {x: 0, y: 1},
                          scale: 1,
                        })
                        .catch(error => {
                          console.error(error);
                        });
                    }}
                  />
                </div>
              </ContentPanel>
            )}
            {sizeAndPositionOptions.advanced && (
              <ContentPanel heading={t('boxpalettes:str_TitleAdvanced')}>
                <div className={'space-y-sm'}>
                  <div className={'flex space-x-md'}>
                    <Label htmlFor="top" label={t('boxpalettes:str_LabelTop')}>
                      <NumberInput
                        data-testid={'box-settings-sizeandposition-top-input'}
                        min={minPositionValues.top}
                        max={minPositionValues.bottom}
                        value={advancedMeasurements.top}
                        id={'top'}
                        step={0.01}
                        size={7}
                        disabled={
                          NumBool.True === item?.protectverticalposition || ui.state.isSmallScreen
                        }
                        submitOnBlur={true}
                        onValueChange={(value: number, interacting: boolean, event: any) => {
                          if (value !== null || event.target.value !== '') {
                            const isGroup = item?.groupref !== 0;
                            const position = isGroup
                              ? (item?.group.box.left as number)
                              : (item?.boxleft as number);
                            const positionWithoutBleed = getPositionWithoutBleed(position);
                            app.core
                              .dispatch(
                                'SHAPE.SET_POSITION',
                                {
                                  y: app.core
                                    .get('utils')
                                    .getBoxCoordinate(value, selectedMeasurment) as number,
                                  x: positionWithoutBleed,
                                  scale: selectedMeasurment,
                                },
                                interacting
                              )
                              .catch(error => {
                                console.error(error);
                              });
                          }
                        }}
                      />
                    </Label>
                    <Label htmlFor="left" label={t('boxpalettes:str_LabelLeft')}>
                      <NumberInput
                        data-testid={'box-settings-sizeandposition-left-input'}
                        min={minPositionValues.left}
                        max={minPositionValues.right}
                        value={advancedMeasurements.left}
                        id={'left'}
                        step={0.01}
                        size={7}
                        disabled={
                          NumBool.True === item?.protecthorizontalposition || ui.state.isSmallScreen
                        }
                        submitOnBlur={true}
                        onValueChange={(value: number, interacting: boolean, event: any) => {
                          if (value !== null || event.target.value !== '') {
                            const isGroup = item?.groupref !== 0;
                            const position = isGroup
                              ? (item?.group.box.top as number)
                              : (item?.boxtop as number);
                            const positionWithoutBleed = getPositionWithoutBleed(position);
                            app.core
                              .dispatch(
                                'SHAPE.SET_POSITION',
                                {
                                  y: positionWithoutBleed,
                                  x: app.core
                                    .get('utils')
                                    .getBoxCoordinate(value, selectedMeasurment) as number,
                                  scale: selectedMeasurment,
                                },
                                interacting
                              )
                              .catch(error => {
                                console.error(error);
                              });
                          }
                        }}
                      />
                    </Label>
                  </div>
                  <div className={'flex space-x-md'}>
                    <Label htmlFor="width" label={t('boxpalettes:str_LabelWidth')}>
                      <NumberInput
                        data-testid={'box-settings-sizeandposition-width-input'}
                        min={minMaxSizeValues.minWidth}
                        max={minMaxSizeValues.maxWidth}
                        value={itemSize.width}
                        version={sizeUpdateTracker.current.version}
                        id={'width'}
                        step={0.01}
                        size={7}
                        disabled={
                          NumBool.True === item?.protecthorizontalsize ||
                          0 !== (item?.groupref as number) ||
                          ui.state.isSmallScreen
                        }
                        submitOnBlur={true}
                        onValueChange={(value: number, interacting: boolean, event: any) => {
                          if (value !== null || event.target.value !== '') {
                            app.core
                              .dispatch(
                                'SHAPE.SET_SIZE',
                                {
                                  width: app.core
                                    .get('utils')
                                    .getBoxCoordinate(value, selectedMeasurment) as number,
                                  height: item?.boxheight as number,
                                },
                                interacting
                              )
                              .then(() => {
                                sizeUpdateTracker.current.version = new Date().getTime();
                              })
                              .catch(error => {
                                console.error(error);
                              });
                          }
                        }}
                      />
                    </Label>
                    <Label htmlFor="height" label={t('boxpalettes:str_LabelHeight')}>
                      <NumberInput
                        data-testid={'box-settings-sizeandposition-height-input'}
                        min={minMaxSizeValues.minHeight}
                        max={minMaxSizeValues.maxHeight}
                        value={itemSize.height}
                        version={sizeUpdateTracker.current.version}
                        id={'height'}
                        step={0.01}
                        size={7}
                        disabled={
                          NumBool.True === item?.protectverticalsize ||
                          0 !== (item?.groupref as number) ||
                          ui.state.isSmallScreen
                        }
                        submitOnBlur={true}
                        onValueChange={(value: number, interacting: boolean, event: any) => {
                          if (value !== null || event.target.value !== '') {
                            app.core
                              .dispatch(
                                'SHAPE.SET_SIZE',
                                {
                                  width: item?.boxwidth as number,
                                  height: app.core
                                    .get('utils')
                                    .getBoxCoordinate(value, selectedMeasurment) as number,
                                },
                                interacting
                              )
                              .then(() => {
                                sizeUpdateTracker.current.version = new Date().getTime();
                              })
                              .catch(error => {
                                console.error(error);
                              });
                          }
                        }}
                      />
                    </Label>
                  </div>
                  <div className={'flex'}>
                    <SelectList
                      data-testid={'box-settings-sizeandposition-measurment-input'}
                      selectedIndex={selectedMeasurment}
                      items={measurementListOptions}
                      labelledBy={'measurementListOptions'}
                      type={'button'}
                      size={'small'}
                      onChange={(event: any) => {
                        setSelectedMeasurement(Number(event.target.value));
                      }}
                    />
                  </div>
                </div>
              </ContentPanel>
            )}
            {sizeAndPositionOptions.lockBoxProportions && (
              <ContentPanel heading={t('boxpalettes:str_TitleBoxProportions')}>
                <Checkbox
                  data-testid={'box-settings-sizeandposition-boxproportions-checkbox'}
                  label={t('boxpalettes:str_LabelLockBoxProportions')}
                  id={'lock-box-proportions'}
                  key={lockProportionsKey}
                  defaultChecked={item?.boxconstrainproportions === 1}
                  appearance={'checkbox'}
                  onChange={(isChecked: boolean) => {
                    if (isChecked) {
                      app.core.dispatch('SHAPE.CONSTRAIN_PROPORTIONS', 1).catch(error => {
                        console.error(error);
                      });
                    } else {
                      app.core.dispatch('SHAPE.CONSTRAIN_PROPORTIONS', 0).catch(error => {
                        console.error(error);
                      });
                    }
                  }}
                  disabled={
                    NumBool.True === item?.protectcontents ||
                    NumBool.True === item?.protectconstrainproportions ||
                    NumBool.True === item?.protecthorizontalsize ||
                    NumBool.True === item?.protectverticalsize
                  }
                />
              </ContentPanel>
            )}
          </>
        )}
      </div>
    </ToolPaletteSection>
  );
};

export default SizeAndPosition;
